import { Component, ElementRef, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { GroupService } from '../../../../core/services/group.service';
import { isStateModeGroup } from '../../../../shared/util/state-mode.util';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { Router } from '@angular/router';
import * as moment from 'moment';
import { Subscription, forkJoin, takeUntil } from 'rxjs';
import { BaseComponent } from './../../../../core/components/base/base.component';
import { AnalyticsService } from './../../../../core/services/analytics.service';
import { FeedbackService } from './../../../../core/services/feedback.service';
import { HospitalService } from './../../../../core/services/hospital.service';
import { PatientAdmissionHistoryService } from './../../../../core/services/patient-admission-history.service';
import { PatientAiService } from './../../../../core/services/patient-ai.service';
import { PatientBillingService } from './../../../../core/services/patient-billing.service';
import { PatientCommunicationService } from './../../../../core/services/patient-communication.service';
import { PatientService } from './../../../../core/services/patient.service';
import { PdfDownloadService } from './../../../../core/services/pdf-download.service';
import { ScrollPositionService } from './../../../../core/services/scroll-position.service';
import { SocketService } from './../../../../core/services/socket.service';
import { UserService } from 'src/app/core/services/user.service';


@Component({
  selector: 'app-patient-admission-record-detail',
  templateUrl: './patient-admission-record-detail.component.html',
  styleUrls: ['./patient-admission-record-detail.component.scss']
})
export class PatientAdmissionRecordDetailComponent extends BaseComponent implements OnInit {

  @ViewChild('buildRecipientListDialog') buildRecipientListDialog?: TemplateRef<any>;


  @ViewChild('dischargeSummaryTextarea', { static: false }) dischargeSummaryTextarea?: ElementRef;
  @ViewChild('patientDischargeSummaryTextarea', { static: false }) patientDischargeSummaryTextarea?: ElementRef;
  @ViewChild(MatMenuTrigger) emailMenuTrigger?: MatMenuTrigger;

  form!: FormGroup;
  patient: any;
  groupId!: string;
  hospitals: any[] = [];
  patientProcedureCodes: any[] | null = [];

  todayDate: moment.Moment = moment();
  private socketSubs: Subscription[] = [];

  public dialogRef!: MatDialogRef<any>;

  editingAh: any;

  userProfile: any;
  canSign: boolean = false;
  isPhysician: boolean = false;
  groupUsers: any[] = [];
  hasSignaturePermission: boolean = false;

  /** A state unit (tasks/state-mode.md) sends nothing: summaries are downloaded instead. */
  get stateUnit(): boolean {
    return isStateModeGroup(this.groupService.group);
  }

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private hospitalService: HospitalService,
    private patientService: PatientService,
    private userService: UserService,
    private ahService: PatientAdmissionHistoryService,
    private patientBillingService: PatientBillingService,
    private pdfDownloadService: PdfDownloadService,
    private socketService: SocketService,
    private aiService: PatientAiService,
    private patientCommunicationService: PatientCommunicationService,
    private scrollService: ScrollPositionService,
    private analyticsService: AnalyticsService,
    private groupService: GroupService,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);

    this.hospitalService.hospitals$
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(hospitals => {
        this.hospitals = hospitals;
      });

    this.patientBillingService.patientBilling$
      .pipe(takeUntil(this.destroy$))
      .subscribe((billing: any[] | null) => {
        this.patientProcedureCodes = billing;
      });

    this.userProfile = this.userService.user;
  }

  ngOnInit() {
    this.initForm();

    this.patientService.currentPatient$
      .pipe(
        takeUntil(this.destroy$)
      )
      .subscribe(patient => {
        this.patient = patient;
        this.form.patchValue({ email: patient.email, name: `${patient.name} ${patient.surname}` });

        if (!this.patient) {
          this.resetForm();
        }
      });

    this.ahService.editingAH$
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (result) {
          this.editingAh = result;
          this.patchForm();
        } else {
          this.resetForm()
        }
      });

    this.isPhysician = this.userService.isUserPhysician;
    this.canSign = this.userService.canUserSign;
    this.hasSignaturePermission = this.userService.isNonPhysician;
    this.groupUsers = this.userService.availableDoctorsList;

  }

  patchForm() {
    if (!this.editingAh) {
      return; // Guard clause in case this.editingNote is not yet set
    }

    this.form.patchValue(this.editingAh);
    this.form.patchValue({ admissionDate: moment(this.editingAh.admissionDate) });
    this.form.patchValue({ dischargeDate: moment(this.editingAh.dischargeDate) });

  }

  resetForm() {
    setTimeout(() => {
      this.patient = null;
      this.editingAh = null;
      this.form.reset();
      this.initForm();
    });
  }


  onRecipientSelected(recipient: {name: string, email: string}) {
    this.form.patchValue({
      name: recipient.name,
      email: recipient.email
    });
  }

  initForm() {
    this.form = this.fb.group({
      admissionDate: ['', Validators.required],
      dischargeDate: ['', Validators.required],
      dischargeSummary: [''],
      patientDischargeSummary: [''],
      dischargeSummaryPrompt: [''],
      patientDischargeSummaryPrompt: [''],
      email: ['', Validators.email],
      name: [''],
      includeSignatureOnPdf: [false],
      includeSignatureOnEmail: [false],
      selectedDoctor: [null],
      pdfDate: [new Date()]
    });

  }


  async saveAdmissionHistory() {

    const dischargeDate = this.form.value.dischargeDate;
    const dischargeMoment = moment.utc(dischargeDate).startOf('day');
    const todayMoment = moment.utc().startOf('day');

    if (dischargeMoment.isBefore(todayMoment)) {
      // Start cleaning up codes from the day after the discharge date
      const startCleanupDate = dischargeMoment.clone().add(1, 'day');
      await this.patientBillingService.checkOverCoding({
        startDate: startCleanupDate.format('YYYY-MM-DD'),
        endDate: todayMoment.format('YYYY-MM-DD')
      }, this.hospitals, this.patient, this.patientProcedureCodes, this);
    }

    this.ahService.updateAdmissionHistory(this.editingAh.id, this.form.value)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.showToastMessage('Admission record updated successfully');
          this.returnToParent();
        },
        error: (error) => {
          console.error('Error updating admission record: ', error);
          this.showToastMessage('There was an error updating the admission record. Please try again.');
        }
      });
  }

  openEmailListCreator() {
    this.dialogRef = this.dialog.open(this.buildRecipientListDialog!, { width: '600px', autoFocus: false });
    return this.dialogRef.afterClosed().toPromise();
  }

  async sendDischargeSummary(sendType: 'patient' | 'doctor') {
    if (sendType === 'doctor') {
      return this.sendDoctorDischargeSummary();
    }
    this.analyticsService.trackButtonClick(`Admission Recored Send Patient Discharge Summary`);
    let { email, patientDischargeSummary, name } = this.form.value;

    const content = patientDischargeSummary;
    const type = 'Discharge Summary';

    if (!email) {
      this.showToastMessage('An email address was not provided. Please check the relevant email field try again.');
      return;
    }

    const plainTextContent = this.pdfDownloadService.convertToPlainText(content)
    const pdfDate = this.form.get('pdfDate')?.value;

    this.patientCommunicationService.sendAIContentEmail({
      patientId: this.patient.id,
      type: type,
      name: name,
      email: email,
      content: plainTextContent,
      speciality: '',
      images: [],
      pdfDate: pdfDate,
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.showToastMessage('Email sent successfully');
          this.emailMenuTrigger?.closeMenu()
        },
        error: (error) => {
          console.error('Error sending AI email: ', error);
          this.showToastMessage('There was an error sending the email. Please try again.');
        }
      });
  }

  async sendDoctorDischargeSummary() {

    const dialogResult = await this.openEmailListCreator();
    if (!dialogResult) return;
    this.analyticsService.trackButtonClick(`Admission Record Send Doctor Discharge Summary`);
    let { dischargeSummary } = this.form.value;
    const requests = []
    for (const recipient of dialogResult) {
      const content = dischargeSummary;
      const type = 'Discharge Summary';
      const plainTextContent = this.pdfDownloadService.convertToPlainText(content);

      const pdfDate = this.form.get('pdfDate')?.value;

      if (this.form.get('includeSignatureOnEmail')?.value && this.isPhysician) {
        const appendSignatureMessage = content;
        requests.push(this.patientCommunicationService.sendAIContentEmail({
          patientId: this.patient.id,
          type: type,
          name: recipient.name,
          email: recipient.email,
          content: appendSignatureMessage,
          speciality: '',
          images: [],
          attachSignature: true,
          signatureUrl: this.userProfile.signatureImageUrl,
          signatoryName: `${this.userProfile.title}. ${this.userProfile.name} ${this.userProfile.surname}`,
          pdfDate: pdfDate,
        }))

      } else if (!this.isPhysician && this.hasSignaturePermission && this.form.get('includeSignatureOnEmail')?.value) {
        const appendSignatureMessage = content;
        requests.push(this.patientCommunicationService.sendAIContentEmail({
          patientId: this.patient.id,
          type: type,
          name: recipient.name,
          email: recipient.email,
          content: appendSignatureMessage,
          speciality: '',
          images: [],
          attachSignature: true,
          signatureUrl: this.form.get('selectedDoctor')?.value.signatureImageUrl,
          signatoryName: `${this.form.get('selectedDoctor')?.value.title}. ${this.form.get('selectedDoctor')?.value.name} ${this.form.get('selectedDoctor')?.value.surname}`,
          pdfDate: pdfDate,
        }))

      } else if (!this.form.get('includeSignatureOnEmail')?.value) {
        requests.push(this.patientCommunicationService.sendAIContentEmail({
          patientId: this.patient.id,
          type: type,
          name: recipient.name,
          email: recipient.email,
          content: content,
          speciality: '',
          images: [],
          pdfDate: pdfDate,
        }))
      }
    }
    forkJoin(requests)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.showToastMessage('Emails sent successfully');
        },
        error: (error) => {
          console.error('Error sending AI emails: ', error);
          this.showToastMessage('There was an error sending the emails. Please try again.');
        }
      });
  }

  aiGenerateDischargeSummary() {
    this.analyticsService.trackButtonClick(`Admission Record Generate Discharge Summary`);
    this.scrollService.scrollTo('details', true)
    this.subscribeToOutputUpdates()
    const { admissionDate, dischargeDate } = this.form.value;
    this.aiService.generateDischargeSummary(this.patient.id, admissionDate.format('DD/MM/YYYY'), dischargeDate.format('DD/MM/YYYY'), { allergies: this.patient.allergies, medicalHistory: this.patient.medicalHistory, medicationHistory: this.patient.medicationHistory })
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  private subscribeToOutputUpdates() {
    let outputStream = '';
    let patientOutputStream = '';

    if (this.socketSubs) {
      for (const sub of this.socketSubs)
        sub.unsubscribe();
      outputStream = '';
      patientOutputStream = '';
    }

    const dischargeSub = this.socketService.listen('aiOutputUpdate-dischargeSummary', 'patient-admission-record-component')
      .pipe(takeUntil(this.destroy$))
      .subscribe(output => {
        outputStream += output.results || '';
        this.form.patchValue({ dischargeSummary: outputStream });
        setTimeout(() => {
          this.dischargeSummaryTextarea!.nativeElement.scrollTop = this.dischargeSummaryTextarea!.nativeElement.scrollHeight;
        }, 0);
        if (output.completed) {
          dischargeSub.unsubscribe();
        }
      });

    const patientDischargeSub = this.socketService.listen('aiOutputUpdate-patientDischargeSummary', 'patient-admission-record-component')
      .pipe(takeUntil(this.destroy$))
      .subscribe(output => {
        patientOutputStream += output.results || '';
        this.form.patchValue({ patientDischargeSummary: patientOutputStream });
        setTimeout(() => {
          this.patientDischargeSummaryTextarea!.nativeElement.scrollTop = this.patientDischargeSummaryTextarea!.nativeElement.scrollHeight;
        }, 0);
        if (output.completed) {
          patientDischargeSub.unsubscribe();
        }
      });

    this.socketSubs.push(
      this.socketService.listen('aiOutputError', 'patient-admission-record-component')
        .subscribe(error => {
          console.error('AI output generation error:', error);
          // Notify user of error
          alert('Error generating AI output.');
        })
    );
  }

  aiPerformUserPrompt(event: Event | null, type: 'dischargeSummary' | 'patientDischargeSummary') {
    this.analyticsService.trackButtonClick(`Admission Record User Prompt For Discharge Summary`);
    event?.preventDefault();
    event?.stopPropagation();
    if ((event as KeyboardEvent)?.shiftKey) return;
    const form = this.form;


    const prompt = form.get(`${type}Prompt`)?.value
    if (!prompt) return;

    const { dischargeSummary, patientDischargeSummary, dischargeDate, admissionDate } = form.value;

    form.get(`${type}Prompt`)?.patchValue('');
    form.get(`${type}Prompt`)?.markAsPristine();

    this.subscribeToOutputUpdates();
    form.get(`${type}`)?.patchValue('');
    this.aiService.performUserDischargePrompt(this.patient.id, admissionDate.clone().format('DD/MM/YYYY'), dischargeDate.clone().format('DD/MM/YYYY'), dischargeSummary, patientDischargeSummary, prompt, type)
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  downloadAsPdf(saveType: 'doctor' | 'patient') {
    this.analyticsService.trackButtonClick(`Admission Record Download PDF For ${saveType}`);
    const content = saveType === 'doctor' ? this.form.get('dischargeSummary')?.value : this.form.get('patientDischargeSummary')?.value;
    if (content[0].trim()) {
      const filename = `MedicAI_${this.patient.name} ${this.patient.surname}_Discharge Summary_${moment().valueOf()}`;

      const pdfDate = this.form.get('pdfDate')?.value;

      if (this.form.get('includeSignatureOnPdf')?.value && this.isPhysician) {
        const appendSignatureMessage = content[0];
        this.pdfDownloadService.downloadPdf({
          content: appendSignatureMessage,
          filename: filename,
          patient: this.patient,
          images: [],
          attachSignature: this.form.get('includeSignatureOnPdf')?.value,
          signatureUrl: this.userProfile.signatureImageUrl,
          signatoryName: `${this.userProfile.title} ${this.userProfile.name} ${this.userProfile.surname}`,
          date: pdfDate,
        });

      } else if (!this.isPhysician && this.hasSignaturePermission && this.form.get('includeSignatureOnPdf')?.value) {
        const appendSignatureMessage = content[0];
        this.pdfDownloadService.downloadPdf({
          content: appendSignatureMessage,
          filename: filename,
          patient: this.patient,
          images: [],
          attachSignature: this.form.get('includeSignatureOnPdf')?.value,
          signatureUrl: this.form.get('selectedDoctor')?.value.signatureImageUrl,
          signatoryName: `${this.form.get('selectedDoctor')?.value.title} ${this.form.get('selectedDoctor')?.value.name} ${this.form.get('selectedDoctor')?.value.surname}`,
          date: pdfDate,
        });

      } else if (!this.form.get('includeSignatureOnPdf')?.value) {
        this.pdfDownloadService.downloadPdf({
          content: content[0],
          filename: filename,
          patient: this.patient,
          images: [],
          attachSignature: false,
          date: pdfDate,
        });

      }
    }
    //this.pdfDownloadService.downloadPdf({ content, filename, patient: this.patient });
  }


  returnToParent() {
    if (this.patient && this.patient.id) {
      this.router.navigate(['/patients', { outlets: { patient: ['file', `${this.patient.id}`] } }]);
    } else {
      this.router.navigate(['/patients', { outlets: { patient: null } }]);
    }
  }

  handleMenuKeydown(event: KeyboardEvent): void {
    if (event.key === 'Tab') {
      event.stopPropagation();
    }
  }
}